import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import ActivityList from '../activities/ActivityList';
import useFetchMoreActivities, {
  ALL_TOPIC_ACTIVITIES_TYPES,
  nextBeforeAt,
} from '../activities/useFetchMoreActivities';
import useAppSelector from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import useAllTopics from '../topic/useAllTopics';

const HOME_ACTIVITIES_QUERY = gql(/** @complexity 14 */ `
  query homeActivitiesV2($topicIds: [ID!]!, $beforeAt: Datetime!, $actions: [TopicActivityAction!]) {
    topicActivities(beforeAt: $beforeAt, topicIds: $topicIds, actions: $actions) {
      ...ActivityList_activities
    }
  }
`);

const HOME_ACTIVITIES_TOPIC_IDS_QUERY = gql(/** @complexity 4 */ `
  query homeActivitiesV2TopicIds {
    currentSession {
      user {
        id
        membershipTopics {
          id
        }
      }
    }
  }
`);

interface UseMyTopicIdsOptions {
  skip: boolean;
}

const useMyTopicIds = ({ skip }: UseMyTopicIdsOptions) => {
  const [shouldQuery, setShouldQuery] = useState(!skip);
  if (!skip && !shouldQuery) {
    setShouldQuery(true);
  }

  const { data: topicIdsData } = useQuery(HOME_ACTIVITIES_TOPIC_IDS_QUERY, {
    skip: !shouldQuery,
  });

  return (
    topicIdsData?.currentSession?.user?.membershipTopics?.map(
      (item) => item.id,
    ) ?? []
  );
};

const HomeActivitiesV2 = () => {
  const isGuest = useAppSelector(
    (state) => state.session.user.type === UserType.Guest,
  );
  const homeActivitiesFilter = useAppSelector(
    (state) => state.ui.homeActivitiesFilter,
  );

  const shouldUseMyTopics = homeActivitiesFilter.topics === 'my' && !isGuest;

  const allTopics = useAllTopics();
  const myTopicIds = useMyTopicIds({ skip: !shouldUseMyTopics });

  const topicIds = shouldUseMyTopics
    ? myTopicIds
    : allTopics.map((topic) => topic.id);

  const actions = isGuest
    ? ALL_TOPIC_ACTIVITIES_TYPES
    : homeActivitiesFilter.actions;

  const variables = {
    topicIds,
    actions,
    beforeAt: nextBeforeAt(),
  };

  const response = useQuery(HOME_ACTIVITIES_QUERY, {
    variables,
    skip: shouldUseMyTopics && !topicIds.length,
  });

  const fetchMore = useFetchMoreActivities(response, 'topicActivities');

  const data =
    response.data?.topicActivities ?? response.previousData?.topicActivities;

  if (data?.length) {
    return (
      <ActivityList
        // Reset ActivityList state when variables change so that the infinite scroll state resets, too
        key={JSON.stringify(variables)}
        activities={data}
        fetchMore={fetchMore}
      />
    );
  }

  return response.loading ? null : (
    <EmptyState title="No activity yet…" vertical />
  );
};

export default HomeActivitiesV2;
